import { useCallback, useEffect, useRef, useState } from "react"
import { QRCodeCanvas } from "qrcode.react"
import { Smartphone, RefreshCw } from "lucide-react"
import { createSelfieLink } from "../../services/selfieService"
import { getMember } from "../../services/memberService"
import { Button } from "../ui/Button"
import { fr } from "../../locales/fr"

/**
 * QR comptoir : le membre scanne et envoie un selfie depuis son téléphone.
 */
export default function SelfieQrPanel({
  memberId,
  currentPhoto = null,
  onPhotoReceived,
  dark = false,
  compact = false,
}) {
  const s = fr.selfie
  const [url, setUrl] = useState("")
  const [expiresAt, setExpiresAt] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [status, setStatus] = useState("") // waiting | received
  const baselinePhoto = useRef(currentPhoto)
  const pollRef = useRef(null)

  useEffect(() => {
    baselinePhoto.current = currentPhoto
    setUrl("")
    setStatus("")
    setError("")
  }, [memberId])

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  useEffect(() => () => stopPoll(), [stopPoll])

  const startPoll = useCallback(() => {
    stopPoll()
    setStatus("waiting")
    pollRef.current = setInterval(async () => {
      try {
        const data = await getMember(memberId)
        const photo = data?.member?.photo ?? null
        if (photo && photo !== baselinePhoto.current) {
          stopPoll()
          setStatus("received")
          setUrl("")
          onPhotoReceived?.(data)
        }
      } catch {
        // ignore
      }
    }, 2500)
  }, [memberId, onPhotoReceived, stopPoll])

  const generate = async () => {
    if (!memberId) return
    setBusy(true)
    setError("")
    setStatus("")
    try {
      baselinePhoto.current = currentPhoto
      const link = await createSelfieLink(memberId)
      setUrl(link.url)
      setExpiresAt(link.expiresAt)
      startPoll()
    } catch {
      setError(s.linkError)
    } finally {
      setBusy(false)
    }
  }

  const box = dark
    ? "rounded-xl border border-slate-600 bg-slate-800/80 p-4 text-slate-100"
    : "rounded-xl border border-border bg-card p-4"

  const muted = dark ? "text-slate-400" : "text-muted-foreground"
  const ok = dark ? "text-emerald-400" : "text-success"

  return (
    <div className={box}>
      <p className={`text-xs font-medium uppercase tracking-wide ${muted}`}>{s.deskTitle}</p>
      <p className={`mt-1 text-sm ${muted}`}>{s.deskHint}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={url ? "outline" : "default"}
          disabled={busy || !memberId}
          className={dark && url ? "border-slate-600 bg-slate-900 text-slate-100" : "gap-2"}
          onClick={generate}
        >
          {url ? <RefreshCw className="h-4 w-4" /> : <Smartphone className="h-4 w-4" />}
          {busy ? s.generating : url ? s.refresh : s.showQr}
        </Button>
      </div>

      {error ? <p className="mt-2 text-sm text-red-400">{error}</p> : null}

      {url ? (
        <div
          className={`mt-4 flex ${compact ? "flex-col" : "flex-col sm:flex-row"} items-center gap-4`}
        >
          <div className="rounded-lg bg-white p-2 ring-1 ring-slate-300">
            <QRCodeCanvas value={url} size={compact ? 140 : 168} />
          </div>
          <div className="min-w-0 space-y-1 text-center sm:text-left">
            <p className={`text-sm ${status === "waiting" ? ok : muted}`}>
              {status === "waiting" ? s.waitingPhone : s.scanHint}
            </p>
            {expiresAt ? (
              <p className={`text-xs ${muted}`}>
                {s.expiresAt.replace(
                  "{{time}}",
                  new Date(expiresAt * 1000).toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                )}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {status === "received" ? (
        <p className={`mt-3 text-sm font-medium ${ok}`}>{s.received}</p>
      ) : null}
    </div>
  )
}
